// Shared types — the contract between the Worker API and the React app.

/** Machine-readable reason a vote was rejected, so the UI can show precise copy
 * (and, for a level gate, the numbers) instead of parsing the message string. */
export type VoteErrorCode = "needs_verification" | "level_too_low" | "poll_closed" | "end_date_required";

export type ApiResponse<T> =
  | { success: true; data: T }
  | { success: false; error: string; code?: VoteErrorCode; requiredLevel?: number; currentLevel?: number };

export type ElectionType = "president" | "congress" | string;

/** Lifecycle phase derived from dates + status. */
export type Phase = "soon" | "live" | "done";

/** A candidate enriched with the stats the estimation engine consumes. */
export interface Candidate {
  userId: string;
  username: string;
  party: string | null;
  /** Live/final votes for this candidate (0 before voting). */
  voteCount: number;
  // --- raw stats (also feed the projection) ---
  level: number;
  careerDamage: number;
  careerDamageRank: number | null;
  premiumMonths: number;
  subscribers: number;
  referrals: number;
  militaryRank: number;
  wealth: number;
  createdAt: string | null;
  lastConnectionAt: string | null;
  isIncumbent: boolean;
  isRulingParty: boolean;
  /** true if this candidate is among the elected (top N by votes) — for finished elections. */
  isElected: boolean;
  hasCampaignArticle: boolean;
  /** All recent posts by this candidate (campaign + others). */
  articles: CampaignArticle[];
  /** Track record in this country's election history. */
  pastRuns: number;
  pastWins: number;
  /** Frozen stats at election time — only for finished elections we observed. */
  snapshot: StatSnapshot | null;
}

export interface StatSnapshot {
  level: number;
  careerDamage: number;
  careerDamageRank: number | null;
  militaryRank: number;
  wealth: number;
  premiumMonths: number;
  subscribers: number;
  referrals: number;
  createdAt: string | null;
  lastConnectionAt: string | null;
}

export interface CampaignArticle {
  id: string;
  title: string;
  excerpt: string;
}

/** One factor's contribution to a candidate's projection score. */
export interface FactorContribution {
  key: string;
  label: string;
  weight: number;
  value: number; // normalized 0..1 (or 0/1 for booleans)
  points: number; // weight * value
}

export interface Projection {
  userId: string;
  username: string;
  score: number;
  winPct: number; // 0..100, sums to 100 across candidates — the raw model output
  /** The displayed forecast: model winPct blended with the crowd poll (backend-computed). */
  forecastPct: number;
  factors: FactorContribution[];
}

export interface PollTally {
  /** userId -> vote count from the crowd poll (our D1). */
  counts: Record<string, number>;
  total: number;
  /** The candidate id this requester voted for (by their voter_hash), or null.
   * Only populated by the per-viewer /poll endpoint — null in the cached forecast. */
  myVote: string | null;
}

export interface ElectionSummary {
  id: string;
  countryId: string;
  countryName: string;
  countryCode: string;
  type: ElectionType;
  phase: Phase;
  votesStartAt: string;
  votesEndAt: string;
  votesCount: number;
  activePopulation: number;
  /** number of people running: in-game candidates once the record exists,
   * else WarUrna pre-declarations for the cycle (deduped across both). */
  candidateCount: number;
  /** projection favorite (always present) */
  favorite: { username: string; winPct: number } | null;
  /** live leader once voting opens */
  liveLeader: { username: string; voteCount: number } | null;
  /** winners once finished — top N by votes (N = seats: 1 president, many congress) */
  winners: { username: string }[];
  /** true for schedule-derived rows (no real WarEra record yet) — non-clickable */
  scheduled: boolean;
}

export interface Forecast {
  election: ElectionSummary;
  candidates: Candidate[];
  projection: Projection[];
  poll: PollTally;
  confidence: "low";
  /** ms epoch — when the WarEra election data was last fetched into our store. */
  updatedAt: number;
}

export interface Leaderboard {
  rows: TurnoutRow[];
  updatedAt: number | null;
}

/** Moderation/visibility of a community poll (mirrors candidacy review). */
export type PollReview = "published" | "hidden";

/** One option of a community poll, with its live vote count. */
export interface PollOption {
  id: string;
  label: string;
  votes: number;
}

/** A generic community poll: anyone creates, anyone votes (anonymous, deduped).
 * `countryId` null = 🌍 global; otherwise scoped to that nation. */
export interface GenericPoll {
  id: string;
  countryId: string | null;
  question: string;
  tag: string | null;
  createdAt: number;
  options: PollOption[];
  total: number;
  /** When true, only verified players (token-bearing) may vote. */
  requireVerified: boolean;
  /** Minimum WarEra level required to vote, or null for no minimum. A non-null
   * value always implies `requireVerified` (level needs a known identity). */
  minLevel: number | null;
  /** Epoch-ms when the poll stops accepting votes, or null for no close time.
   * Closed when non-null and `<= now`. A manual close writes this to "now". */
  endDate: number | null;
  /** Server-computed for the requesting device: true when the requester is the
   * verified owner and the poll is still open — i.e. show them a "close" button.
   * Never exposes the owner's identity to other viewers. */
  closeable: boolean;
  /** The option id this requester voted for, recomputed from their voter_hash
   * (verified user id, else IP), or null if they haven't voted. Lets a returning
   * voter see their pick + revealed results across reloads/devices. */
  myVote: string | null;
}

/** The kind of event in a country's activity wire. */
export type ActivityKind =
  | "candidacy"
  | "candidacy_update"
  | "ama_question"
  | "ama_answer"
  | "article"
  | "poll";

/**
 * One item in a nation's "Latest in {nation}" wire. Derived from existing tables
 * (new candidacies, answered AMA questions, campaign posts, new community polls).
 * Carries the kind-specific ids the client needs to deep-link back to the source.
 */
export interface ActivityEvent {
  kind: ActivityKind;
  /** ms epoch of the event (recent-first). */
  at: number;
  countryId: string;
  /** the candidacy this event belongs to — set for candidacy/ama/article. */
  candidacyId: string | null;
  office: CandidacyOffice | null;
  /** the poll this event belongs to — set for poll events. */
  pollId: string | null;
  /** the actor — the candidate's username; null for anonymous community polls. */
  username: string | null;
  /** extra context: answered-question snippet, article title, or poll question. */
  detail: string | null;
}

/**
 * A lean cross-country election row for the "Around the World" feed. Built
 * straight from the D1 election store (no projection / user fetch), so it omits
 * the favorite/leader/winner names — open the election for the full forecast.
 */
export interface WorldElection {
  id: string;
  countryId: string;
  countryName: string;
  countryCode: string;
  type: ElectionType;
  phase: Phase;
  votesStartAt: string;
  votesEndAt: string;
  votesCount: number;
  activePopulation: number;
}

export interface CountryLite {
  id: string;
  name: string;
  code: string;
}

export interface TurnoutRow {
  countryId: string;
  countryName: string;
  countryCode: string;
  electionId: string;
  electionType: ElectionType;
  voted: number;
  eligible: number;
  turnoutPct: number;
  abstentionPct: number;
}

// --- Pre-declared candidacies (user-generated, before WarEra opens candidacy) ---

/** The office a candidacy targets — the two scheduled race types. */
export type CandidacyOffice = "congress" | "president";

/**
 * Moderation / visibility axis. `published` = publicly visible; `pending` =
 * awaiting admin approval (scraped pages start here, never public until
 * approved); `hidden` = removed/rejected by an admin.
 */
export type CandidacyReview = "pending" | "published" | "hidden";

/**
 * Provenance axis (the public badge). `imported` = scraped + auto-summarized
 * from the player's WarEra campaign article; `declared` = first-party content
 * the candidate wrote themselves. An `imported` campaign flips to `declared` the
 * moment its owner edits it. (Trust is NOT here — that's the owning user's
 * {@link ClaimStatus}.)
 */
export type CandidacyOrigin = "imported" | "declared";

/**
 * Whether the WarEra user behind a campaign has proved they control that account
 * (via the company-name ownership proof). `claimed` users hold an edit token and
 * can manage every campaign they own; `unclaimed` ones have not verified yet.
 * Derived from the `user_claims` table — never stored on the candidacy.
 */
export type ClaimStatus = "claimed" | "unclaimed";

/** One plank of a candidate's platform. */
export interface Measure {
  id: string;
  title: string;
  detail: string | null;
  /** tiptap JSON (rich detail); null for pre-backfill rows (fall back to `detail`). */
  detailDoc: string | null;
}

/** A cited source for an imported candidacy (the WarEra article the summary
 * was drawn from). `label` is an optional human title for the link. */
export interface CandidacySource {
  url: string;
  label: string | null;
}

/** An AMA question with the candidate's (optional) answer. */
export interface AmaQuestion {
  id: string;
  body: string;
  /** tiptap JSON (rich question body); null for pre-backfill rows (fall back to `body`). */
  bodyDoc: string | null;
  /** optional self-reported asker name; null = anonymous. */
  askerName: string | null;
  answer: string | null;
  /** tiptap JSON (rich answer); null when unanswered or pre-backfill (fall back to `answer`). */
  answerDoc: string | null;
  /** ms epoch when answered, or null while pending. */
  answeredAt: number | null;
  upvotes: number;
  /** ms epoch the question was asked. */
  createdAt: number;
}

/** Public view of a declared candidacy. */
export interface Candidacy {
  id: string;
  countryId: string;
  office: CandidacyOffice;
  /** ms epoch of the target race (cycle key). */
  cycleStartAt: number;
  username: string;
  /** WarEra user id once reconciled, else null. Enables a profile deep-link. */
  userId: string | null;
  /** optional party affiliation; null = independent. */
  party: string | null;
  intro: string | null;
  /** tiptap JSON (rich manifesto); null for pre-backfill rows (fall back to `intro`). */
  introDoc: string | null;
  /** moderation/visibility state. */
  review: CandidacyReview;
  /** provenance badge (imported vs first-party). */
  origin: CandidacyOrigin;
  /** whether the owning WarEra user has verified ownership (derived). */
  claimStatus: ClaimStatus;
  /** real WarEra election id once reconciled, else null. */
  linkedElectionId: string | null;
  /** cited source article(s) for an imported candidacy; empty for self-declared. */
  sources: CandidacySource[];
  createdAt: number;
  measures: Measure[];
}

/** Live WarEra stats for the player behind a campaign — the same stat set the
 * forecast shows. Null fields where the WarEra API didn't report a value. */
export interface CandidacyStats {
  level: number;
  careerDamage: number;
  careerDamageRank: number | null;
  militaryRank: number;
  wealth: number;
  premiumMonths: number;
  subscribers: number;
  referrals: number;
  createdAt: string | null;
  lastConnectionAt: string | null;
}

/** Candidacy detail = the public candidacy plus its AMA wall. */
export interface CandidacyDetail extends Candidacy {
  questions: AmaQuestion[];
  /** true when the requester's per-user edit token owns this campaign — gates the
   * edit/answer affordances to the candidate's own page. */
  mine: boolean;
  /** live stats for the owning player; null when the owner is unresolved or the
   * WarEra API has no record yet. */
  stats: CandidacyStats | null;
}

/** Returned on declaration. Editing is unlocked separately by *claiming* (proving
 * WarEra ownership) — declaration no longer mints a per-campaign secret. */
export interface CandidacyCreated {
  candidacy: Candidacy;
}

/** One candidacy as listed on a user profile (no platform body — the nested
 * candidacy page carries that). */
export interface ProfileCandidacy {
  id: string;
  office: CandidacyOffice;
  countryId: string;
  countryName: string | null;
  cycleStartAt: number; // ms epoch (fromDbTime), like Candidacy.cycleStartAt
  party: string | null;
  origin: CandidacyOrigin;
  review: CandidacyReview; // non-published rows only reach the owner/admin
  electionId: string | null;
}

/** One election a user ran in, as shown on their profile. */
export interface ProfileElection {
  electionId: string;
  countryId: string;
  countryName: string | null;
  type: ElectionType;
  endAt: string; // ISO (we_elections votesEndAt)
  phase: Phase;
  voteCount: number;
  rank: number; // 1-based by final vote count
  totalCandidates: number;
  seats: number; // electedCount (1 president, N congress)
  elected: boolean;
  candidacyId: string | null; // our campaign page for this run, when we have one
}

/** Full payload for GET /api/users/:username/profile — the /u/:username page. */
export interface UserProfile {
  userId: string;
  username: string;
  claimStatus: ClaimStatus | null; // null = not a candidate user (plain player)
  stats: CandidacyStats | null;
  candidacies: ProfileCandidacy[]; // newest cycle first
  elections: ProfileElection[]; // newest end date first
}

/** One entry in the admin security audit trail. */
export interface AuditEntry {
  id: string;
  /** ms epoch of the event. */
  at: number;
  ip: string | null;
  action: string;
  target: string | null;
  /** true = authorized action performed; false = rejected attempt. */
  ok: boolean;
  detail: string | null;
}

/** Admin view of a candidate user (a WarEra player who holds a candidacy).
 * `verified` ⇔ candidate_users.status = 'claimed'. */
export interface AdminUser {
  /** WarEra user id (candidate_users.id). */
  id: string;
  username: string;
  status: ClaimStatus;
  /** whether a per-user edit token is currently set (status may be claimed yet
   * tokenless after a full revoke, so this is reported separately). */
  hasToken: boolean;
  /** how many candidacies are linked to this user. */
  candidacyCount: number;
  createdAt: number;
  updatedAt: number;
}

/** Admin view of an AMA question with the candidacy context needed to moderate
 * it without first opening the campaign page. */
export interface AdminQuestion {
  id: string;
  candidacyId: string;
  candidacyUsername: string;
  countryId: string;
  office: CandidacyOffice;
  body: string;
  askerName: string | null;
  answer: string | null;
  upvotes: number;
  hidden: boolean;
  createdAt: number;
}

/** The candidacy list for one upcoming race, with its resolved cycle date. */
export interface RaceCandidacies {
  office: CandidacyOffice;
  /** ms epoch of the upcoming race this list belongs to. */
  cycleStartAt: number;
  /** seats this race elects (president = 1, congress = WarEra electedCount); null if unknown. */
  seats: number | null;
  candidacies: Candidacy[];
}

/** One equipped slot, as the gear strip draws it.
 *
 * Rarity is an index into the game's six-tier ramp (common, uncommon, rare,
 * epic, legendary, mythic) and is null when the config does not know the item's
 * code: painting an unknown item as common would be a guess that reads as a
 * fact. Durability is the share of the item's condition left, and a slot near
 * zero is about to stop contributing its skills at all. */
/** The slots the game equips, in the order the gear strip draws them. Shared so
 * the worker that builds the strip and the surfaces that draw it cannot fall
 * into a different order. */
export const MU_GEAR_SLOTS = ["weapon", "helmet", "chest", "pants", "boots", "gloves"] as const;

export interface MuGearSlot {
  slot: "weapon" | "helmet" | "chest" | "pants" | "boots" | "gloves";
  code: string;
  rarity: number | null;
  rarityName: string | null;
  /** What the item adds, keyed by combat skill. */
  skills: Record<string, number>;
  state: number | null;
  maxState: number | null;
  durability: number | null;
  nearlyBroken: boolean;
}

/** One MU member as the queue panel renders them. All numbers are computed
 * server-side so the extension stays a dumb renderer. */
export interface MuRosterMember {
  userId: string;
  username: string;
  damagePerHit: number;
  currentHealth: number;
  /** Full health bar, for showing current health in proportion. */
  maxHealth: number;
  hitsAvailable: number;
  /** Hits the member can pay for once food is counted, and the damage that
   * buys. Hunger, not the health bar, is what really bounds a contract. */
  hitsCapacity: number;
  projectedDamage: number;
  /** Damage from the current bar alone, before eating. What "ready now" means
   * to a queue deciding who to call next. */
  readyDamage: number;
  /** Hunger points left and the full bar. Each point buys one food, and each
   * food restores a slice of the health bar. */
  hunger: number;
  maxHunger: number;
  lastConnectionAt: string | null;
  online: boolean;
  alreadyHitThisRound: boolean;
  /** Health cost of one hit, and how fast health comes back. Together they
   * answer "when can this member hit again", which is what a queue needs when
   * somebody is out of health right now. */
  healthCostPerHit: number;
  healthRegenPerHour: number;
  /** The player's avatar, or null. Only https URLs survive: the panel puts this
   * straight into an img tag. */
  avatarUrl: string | null;
  /** The player's level, or null when the game did not say. A token beside the
   * face, never a damage claim: what a member can deal is already in their own
   * figures, and this is the one fact about them that says how long they have
   * been at it. */
  level: number | null;
  /** What this member is wearing, in the order the gear strip draws it. Empty
   * when nothing is equipped; absent slots are simply missing rather than
   * reported as the weakest tier. Context on who is equipped for what, never a
   * damage rule: the damage figures already include every contribution gear
   * makes, and nothing in the game caps a set at its worst slot. */
  gear?: MuGearSlot[];
  /** False when the equipment lookup failed for this member, so the strip can
   * say it does not know instead of drawing an unequipped fighter. */
  gearKnown?: boolean;
  /** Active pill (buff) codes, when the member is running one. */
  buffCodes: string[];
  /** When the current pill wears off. Damage per hit already includes the
   * pill, so this is when that number drops. */
  buffEndsAt: string | null;
  /** Attack bonus the pill is contributing, in percent. */
  buffPercent: number;
  /** Attack penalty currently applied, in percent. Damage per hit already
   * reflects it; the panel shows it so a low number explains itself. */
  debuffPercent: number;
  /** What is causing the penalty, and when it lifts. */
  debuffCodes: string[];
  debuffEndsAt: string | null;
  /** Damage this member has contributed since the contract started. Zero when
   * there is no contract, or when they have not hit in that battle yet. */
  contractDamage: number;
  /** What the contract pays this member, as the game splits it. Null until a
   * logged-in panel has relayed the contract feed, which is the only place the
   * split is stated. */
  contractPayout?: number | null;
  /** This member's Discord account id, when somebody has filled it in. It is
   * what makes the message copied into a channel actually notify them; without
   * it they are named as text, which pings nobody. */
  discordId?: string | null;
  /** True when the game refused this member on the last roster build and the
   * card is carried over from the previous one. Their health and online status
   * are as of that earlier read; everything else about them still holds. */
  stale?: boolean;
  /** When the game last refused this member, so a card that is being carried
   * cannot be carried forever: an id the game will never answer for again (a
   * closed account still listed by the unit) eventually drops off. */
  staleSinceMs?: number;
}

export interface MuQueueEntry {
  userId: string;
  username: string;
  slot: number;
  joinedAt: string;
  /** Damage this member committed to, when they overrode the calculated one. */
  pledgedDamage: number | null;
  /** Who queued them. Equal to userId when they queued themselves. */
  addedBy: string | null;
}

/**
 * Why somebody is no longer in the queue, kept for the member it happened to.
 *
 * A removal used to exist only as the difference between two polls, so it was
 * invisible to whoever was away when it happened — which, for the offline drop,
 * is everybody it happens to.
 */
export interface MuQueueRemoval {
  userId: string;
  /** The member who took them off, or null when the service's own offline rule
   * did. Never guessed: an unauthored removal says so. */
  removedBy: string | null;
  /** "teammate" is a person's decision; "offline" is the drop rule, which has
   * to explain itself because from the member's side the two look identical. */
  reason: "teammate" | "offline";
  /** What they were in for, so the notice can offer to put it back unchanged. */
  committed: number;
  removedAt: string;
}

/** How a contract row arrived. Only "typed" is a plan rather than an
 * obligation: somebody set a target to work towards, and nothing was won. */
export type MuContractSource = "typed" | "auction" | "game" | "link";

export interface MuContractView {
  /** Battle damage bonus in percent, when set by hand. */
  bonusPercent: number | null;
  /** Who typed that percentage and when, so a surface about to replace it can
   * name whose figure it is. Null on rows written before the columns existed:
   * we do not know, and no copy may imply that we do. */
  bonusSetBy?: string | null;
  bonusSetAt?: string | null;
  /** "auction" when adopted from a won mercenary contract, otherwise the id of
   * the member who typed it. */
  createdBy: string;
  fromAuction: boolean;
  /** Where the row came from, and whether that means the unit holds anything.
   * An unconfirmed contract reports no progress and records no damage. */
  source: MuContractSource;
  confirmed: boolean;
  /** The game's own id for this contract, when it named one. Two contracts can
   * run on one battle, and this is what tells them apart. */
  gameRef: string | null;
  battleId: string;
  targetDamage: number;
  createdAt: string;
  roundId: string | null;
}

export interface MuProgressView {
  totalDamage: number;
  hitCount: number;
  lastHitAt: string | null;
  remainingDamage: number;
  reached: boolean;
  /** "game" when a logged-in panel relayed the contract's own counter, which
   * needs no baseline and misses nothing. "estimate" when this is our own
   * arithmetic over battle loot totals and the scraped hit log. */
  source: "game" | "estimate";
  /** When the counter behind `totalDamage` was last relayed, epoch-ms.
   *
   * A relayed figure is only as current as the last member who had the game
   * open: with every tab shut it stands still while continuing to look like
   * the truth. Null when the figure is our own arithmetic, which is computed
   * for this request and so is as fresh as the response itself. */
  updatedAt: number | null;
}

/** The damage multiplier this battle is applying, measured from what members
 * actually dealt. The game stacks country/MU order, patriotic, alliance and
 * resistance bonuses without publishing the total, and a player's own attack
 * figure excludes all of them. */
export interface MuBattleBonus {
  /** The multiplier actually applied to projections. */
  factor: number;
  /** How many members the measurement drew on. */
  sampleSize: number;
  /** What produced `factor`: a figure somebody typed, a measurement, or
   * nothing yet.
   *
   * Once a measurement exists it wins, because a typed percentage is only ever
   * a seed for underwriting and what members actually dealt is the fact. */
  source: "set" | "measured" | "none";
  /** What the measurement says. Equal to `factor` whenever one exists. */
  measuredFactor: number | null;
  /** The hand-typed seed, still reported after a measurement supersedes it, so
   * both surfaces can say WHAT was replaced rather than swapping the number in
   * silence. Null when nobody typed one. */
  setFactor: number | null;
}

/**
 * A contract as the game itself lists it, relayed by a logged-in panel.
 *
 * This is the only description of a contract that states who dealt what and who
 * earned what. Everything else the queue knows about damage is reconstructed
 * from public battle data.
 */
export interface MuFeedContract {
  /** The game's own id for the contract. */
  gameRef: string;
  battleId: string;
  roundId: string | null;
  /** The game's word for it: "active", "completed", "failed"... */
  status: string;
  targetDamage: number;
  currentDamage: number;
  /** Total money the contract pays out, and the rate it pays per 1k damage. */
  moneyPool: number | null;
  moneyPer1k: number | null;
  /** userId -> damage dealt toward this contract. */
  damagesByUser: Record<string, number>;
  /** userId -> payout for this contract. */
  moneyPerUser: Record<string, number>;
  wonAt: string | null;
  completedAt: string | null;
  /** When this was last relayed to us, epoch-ms. */
  updatedAt: number;
}

/** Everything the MU queue panel needs, in one response. */
export interface MuState {
  muId: string;
  muName: string;
  roster: MuRosterMember[];
  queue: MuQueueEntry[];
  contract: MuContractView | null;
  progress: MuProgressView | null;
  /**
   * Where the unit stands, which damage figures alone cannot say:
   * - "idle": no contract at all, the queue is people lining up for nothing yet
   * - "won": a contract is held but no damage has been counted toward it
   * - "live": damage is landing against it
   * - "done": the game called it completed, or the target has been met
   */
  phase: "idle" | "planning" | "won" | "live" | "done";
  rosterFetchedAt: number;
  /**
   * When this service last heard anything from a logged-in member of this unit,
   * epoch-ms, or null when none ever has.
   *
   * Unit-scoped and contract-independent on purpose. `progress.updatedAt` is
   * contract-scoped and null whenever there is no contract, which is the idle
   * screen and the most-visited one, so it can never distinguish "nothing is
   * happening" from "nobody has the game open and we cannot tell".
   */
  relayedAt: number | null;
  /**
   * Removals recent enough that the member they happened to may not have heard.
   * Unit-scoped rather than caller-scoped because /state has no caller: each
   * surface picks out the row that is about the person reading it.
   */
  removals: MuQueueRemoval[];
  battleBonus: MuBattleBonus;
  /** Which battle the unit is looking at, and who says they pointed it there.
   * Null when nobody has, which is the ordinary state of a unit between
   * contracts rather than a fault. */
  target: MuTargetView | null;
  /** The expected-damage figure somebody typed for the targeted battle, so a
   * queue with no contract still has something to be judged against. Null when
   * nobody typed one. */
  handTarget: number | null;
  /** The battles worth targeting: every battle with an open contract, plus the
   * one the unit is aimed at and the one it is fighting in. Battle first, then
   * contract: the reader picks the fight before the paperwork. */
  battles: MuBattleOption[];
  /** The contracts open in each battle, keyed by battle id. A battle the unit
   * already holds a contract in lists only that contract (ruling 5), so the
   * screen stops offering work the unit cannot take. */
  contracts: Record<string, MuAuctionOption[]>;
  /** What the unit owes across every contract it is fulfilling, against the one
   * pool of commitments it holds. Stated once here so no surface derives it. */
  unitWide: MuUnitWideTotals;
  /** The first roster fetch for this MU is still running. Members are empty
   * because we do not know them yet, not because the unit is empty. */
  warming: boolean;
}

/**
 * Where this unit stands with one auction.
 *
 * Derived, never read off the wire: the game's own statuses say what happened to
 * the auction, not what this unit's relationship to it is. `settled` is one word
 * for every ending that is nobody's defeat; `lost` needs a bid of ours.
 */
export type MuAuctionPhase = "open" | "bid" | "fulfilling" | "lost" | "settled";

/** A battle the reader can aim the unit at. */
export interface MuBattleOption {
  battleId: string;
  /** How many contracts this battle is offering the unit. */
  contractCount: number;
  /** The unit already holds a contract here, so this battle is where the work
   * is rather than where the shopping is. */
  held: boolean;
  /** The measured damage multiplier for this battle, or null when nothing has
   * been measured. Null reads `bonus unknown` and never a fabricated 0 percent:
   * the game never states the stacking bonuses, so an unfought battle has no
   * figure to state (see the spec's data questions). */
  bonusFactor: number | null;
}

/** One contract on offer, as the contract list shows it. */
export interface MuAuctionOption {
  auctionId: string;
  battleId: string;
  /** The country that posted it. */
  poster: string | null;
  expectedDamage: number;
  ratePerK: number | null;
  payout: number | null;
  /** The acceptance fee the winning unit stakes, computed from the payout
   * because the auction record carries no deposit field. Null when the game
   * stated no payout to take a fee from. */
  deposit: number | null;
  /** When the auction closes, epoch-ms, or null when the game stated none. The
   * countdown ticks client-side from here: these auctions run five to
   * twenty-five minutes and a poll is fifteen seconds. */
  closesAtMs: number | null;
  /** Inside the window where a bid pushes the close out, so a clock that jumps
   * forward can be explained rather than read as a bug. */
  extendable: boolean;
  professionalsOnly: boolean;
  /** Whether this unit could bid, and the caveat or the reason it could not.
   * An auction the unit cannot bid on must never read as available. */
  canBid: boolean;
  bidReason: string | null;
  phase: MuAuctionPhase;
}

/**
 * The one figure that keeps a unit holding several contracts honest.
 *
 * Coverage is per contract while the queue is one pool, so two contracts being
 * fulfilled can each read "queue covers it" off the same commitments while the
 * unit is short overall.
 */
export interface MuUnitWideTotals {
  /** Whether the line is shown at all. One contract needs no reconciling. */
  manyLive: boolean;
  liveCount: number;
  committed: number;
  expected: number;
  shortOverall: number;
}

/** The battle the unit is aimed at.
 *
 * `setBy` is attribution, not authentication: MU writes are open, so this is who
 * claimed to set it. Surfaces may name them, and may not imply it was proved.
 * `source` separates a deliberate pick from a battle read off the game page a
 * member happened to have open. */
export interface MuTargetView {
  battleId: string;
  setBy: string | null;
  setAtMs: number;
  source: "pick" | "page";
}
